import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { EditorState, RichUtils } from 'draft-js';
import {
  changeDepth,
  getBlockBeforeSelectedBlock,
  getSelectedBlock,
  isListBlock,
} from 'draftjs-utils';

import { IndentIcon } from '../../icons/IndentIcon';
import { ListOrderedIcon } from '../../icons/ListOrderedIcon';
import { ListUnorderedIcon } from '../../icons/ListUnorderedIcon';
import { OutdentIcon } from '../../icons/OutdentIcon';
import { ToolButton } from '../common/ToolButton';
import { ToolGroup } from '../common/ToolGroup';

enum ListType {
  Ordered = 'ordered-list-item',
  Unordered = 'unordered-list-item',
}

enum IndentationChange {
  Indent = 'indent',
  Outdent = 'outdent',
}

interface Props {
  onChange: (editorState: EditorState) => void;
  editorState: EditorState;
}

export function List({ onChange, editorState }: Props) {
  const intl = useIntl();

  const currentBlock = useMemo(
    () => (editorState ? getSelectedBlock(editorState) : undefined),
    [editorState]
  );

  const indentDisabled = useMemo(() => {
    const previousBlock = getBlockBeforeSelectedBlock(editorState);

    return (
      !currentBlock ||
      !previousBlock ||
      !isListBlock(currentBlock) ||
      previousBlock.getType() !== currentBlock.getType() ||
      previousBlock.getDepth() < currentBlock.getDepth()
    );
  }, [currentBlock, editorState]);

  const outdentDisabled = useMemo(
    () => !currentBlock || !isListBlock(currentBlock) || currentBlock.getDepth() <= 0,
    [currentBlock]
  );

  const currentListType = useMemo(() => currentBlock?.getType(), [currentBlock]);

  const handleListTypeChanged = (newType: ListType) => {
    onChange(RichUtils.toggleBlockType(editorState, newType));
  };

  const handleIndentationChange = (operationType: IndentationChange) => {
    onChange(changeDepth(editorState, operationType === IndentationChange.Indent ? 1 : -1, 4));
  };

  return (
    <ToolGroup aria-label="rdw-list-control">
      <ToolButton
        aria-pressed={currentListType === ListType.Unordered}
        onClick={() => handleListTypeChanged(ListType.Unordered)}
        title={intl.formatMessage({ id: 'wysiwygEditor.list.unordered' })}
      >
        <ListUnorderedIcon />
      </ToolButton>

      <ToolButton
        aria-pressed={currentListType === ListType.Ordered}
        onClick={() => handleListTypeChanged(ListType.Ordered)}
        title={intl.formatMessage({ id: 'wysiwygEditor.list.ordered' })}
      >
        <ListOrderedIcon />
      </ToolButton>

      <ToolButton
        disabled={indentDisabled}
        onClick={() => handleIndentationChange(IndentationChange.Indent)}
        title={intl.formatMessage({ id: 'wysiwygEditor.list.indent' })}
      >
        <IndentIcon />
      </ToolButton>

      <ToolButton
        disabled={outdentDisabled}
        onClick={() => handleIndentationChange(IndentationChange.Outdent)}
        title={intl.formatMessage({ id: 'wysiwygEditor.list.outdent' })}
      >
        <OutdentIcon />
      </ToolButton>
    </ToolGroup>
  );
}
